import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, RefreshCw } from 'lucide-react';
import {
  SURVEY_STATUSES,
  type SurveyDto,
  type SurveyStatus,
} from '@eombor/contracts';
import { Badge, Button, Card, EmptyState, IconButton, Skeleton, cn, useToast } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Select } from '../../../shared/ui/Select.js';
import { ApiClientError } from '../../../shared/api/client.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';
import { useOrgUnits } from '../../org/model/org.queries.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import { useRequisition, useTransition } from '../../requisition/model/requisition.queries.js';
import { requisitionErrorText } from '../../requisition/model/requisition-error.js';
import { accountingApi, docFileName } from '../api/accounting.api.js';
import { accountingKeys, useSurveys } from '../model/accounting.queries.js';
import { formatDate, surveyStatusTone } from '../model/format.js';
import { downloadBlob } from '../../../shared/api/download.js';
import { SurveyReviewDialog } from './SurveyReviewDialog.js';
import { SurveyCard, type SurveyCardLine } from './SurveyCard.js';
import { surveyStatusText, unitText } from '../../../shared/i18n/labels.js';

/**
 * So'rovnomalar ekrani — buxgalterning NAVBATI.
 *
 * Chapda navbat, o'ngda tanlangan hujjat: buxgalter kun davomida o'nlab
 * so'rovnomani ko'zdan kechiradi va har biri uchun alohida sahifaga o'tib,
 * keyin orqaga qaytish ish tezligini yarmiga tushirardi.
 *
 * Tasdiqlash va rad etish ARIZA o'tishi orqali yuboriladi
 * (`POST /requisitions/:id/transition`) — buxgalteriyada bunday endpoint yo'q
 * va bo'lmaydi ham: hujjat talabnoma holatidan ajralib qolmasligi kerak.
 */

/** Buxgalter birinchi navbatda O'Z navbatini ko'radi — ochiq so'rovnomalarni. */
const DEFAULT_STATUS: SurveyStatus | '' = 'PENDING';

interface QueueRowProps {
  survey: SurveyDto;
  selected: boolean;
  onSelect: () => void;
}

const QueueRow = ({ survey, selected, onSelect }: QueueRowProps) => (
  <li>
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'flex w-full flex-col gap-1 rounded-[var(--radius-sm)] border px-3 py-2 text-left',
        'transition-[background-color,border-color] duration-[var(--motion-fast)]',
        'ease-[var(--ease-standard)]',
        selected
          ? 'border-accent bg-accent-soft'
          : 'border-transparent hover:border-border hover:bg-surface-2',
      )}
    >
      {/* Hujjat raqami hech qachon o'ralmaydi: navbatda odam aynan shu
          raqamga qarab hujjatni topadi, ikkiga bo'lingan raqam esa o'qishni
          sekinlashtiradi. Holat yorlig'i uzun, shuning uchun o'z qatorida. */}
      <span className="flex items-baseline justify-between gap-2">
        {/* Navbatda ham QOG'OZDAGI raqam turadi: buxgalter hujjatni
            aynan shu raqam bilan izlaydi. To'ldirilmagan bo'lsa tizim
            raqami xira ko'rinadi — u vaqtinchalik belgi. */}
        <span className="tabular whitespace-nowrap text-sm font-semibold text-text">
          {survey.formNo ?? (
            <span className="font-normal text-text-faint">{survey.docNo}</span>
          )}
        </span>
        <span className="tabular shrink-0 text-xs text-text-faint">
          {formatDate(survey.createdAt) ?? ''}
        </span>
      </span>
      <span className="tabular truncate text-xs text-text-faint">
        {survey.requisitionDocNo}
      </span>
      <Badge tone={surveyStatusTone(survey.status)} className="self-start">
        {surveyStatusText(survey.status)}
      </Badge>
    </button>
  </li>
);

export const SurveysPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = usePermission();

  const [status, setStatus] = useState<SurveyStatus | ''>(DEFAULT_STATUS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pdfPendingId, setPdfPendingId] = useState<string | null>(null);
  /** Ochiq trebovaniya blanki — so'rovnoma id si. */
  const [requirementId, setRequirementId] = useState<string | null>(null);

  const list = useSurveys({ ...(status === '' ? {} : { status }), limit: 50 });
  const items = useMemo(() => list.data?.items ?? [], [list.data]);

  const selected = items.find((row) => row.id === selectedId) ?? null;

  /* Navbat yangilanganda tanlov yo'qolib qolmasligi kerak: tasdiqlangan
     so'rovnoma "ochiq" filtridan chiqib ketadi va ekranning o'ng yarmi
     bo'shab qoladi — shunda birinchi hujjat ochiladi. */
  useEffect(() => {
    if (items.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (items.some((row) => row.id === selectedId)) return;
    setSelectedId(items[0]?.id ?? null);
  }, [items, selectedId]);

  const requisition = useRequisition(selected?.requisitionId ?? null);
  const transition = useTransition(selected?.requisitionId ?? '');

  const warehouses = useWarehouses({ limit: 200 });
  const nomenclatures = useNomenclatures({ limit: 200 });
  const orgUnits = useOrgUnits();

  /** Ombor nomi identifikator o'rniga — ikki joyda kerak. */
  const warehouseNameOf = (id: string): string | undefined =>
    (warehouses.data?.items ?? []).find((row) => row.id === id)?.name;

  const nomenclatureOf = useMemo(
    () => new Map((nomenclatures.data?.items ?? []).map((row) => [row.id, row])),
    [nomenclatures.data],
  );

  const lines: SurveyCardLine[] = (requisition.data?.lines ?? []).map((line) => {
    const nomenclature = nomenclatureOf.get(line.nomenclatureId);
    return {
      id: line.id,
      lineNo: line.lineNo,
      name: nomenclature?.name ?? `#${line.nomenclatureId}`,
      code: nomenclature?.code ?? null,
      unit: nomenclature === undefined ? null : unitText(nomenclature.unit),
      requestedQty: line.requestedQty,
      approvedQty: line.approvedQty,
    };
  });

  /* Ruxsat AYNAN shu hujjat ustida tekshiriladi: buxgalterga odatda bitta
     MTU yoki korxona doirasi beriladi, shuning uchun maqsadsiz tekshiruv
     ("umuman ruxsat bormi") begona hujjat ustida ham tugma chizib qo'yardi. */
  const target =
    selected === null
      ? {}
      : {
          mtuId: selected.mtuId,
          warehouseId: selected.warehouseId,
          ...(selected.orgUnitId === null ? {} : { orgUnitId: selected.orgUnitId }),
        };

  const errorText =
    transition.error === null || transition.error === undefined
      ? null
      : requisitionErrorText(transition.error, t('accounting.decisionFailed'));

  const decide = (to: 'ACCOUNTING_APPROVED' | 'REJECTED', reason?: string): void => {
    if (selected === null) return;
    const docNo = selected.docNo;
    transition.mutate(
      { to, ...(reason === undefined ? {} : { reason }) },
      {
        onSuccess: () => {
          // Hujjatlar o'tishning effekti sifatida o'zgardi — navbat ham,
          // prixod ro'yxati ham eskirdi.
          void queryClient.invalidateQueries({ queryKey: accountingKeys.all });
          toast.show(
            t(to === 'ACCOUNTING_APPROVED' ? 'accounting.approved' : 'accounting.rejected', {
              docNo,
            }),
            'success',
          );
        },
      },
    );
  };

  const downloadPdf = (survey: SurveyDto): void => {
    setPdfPendingId(survey.id);
    accountingApi
      .surveyPdf(survey.id)
      .then((blob) => {
        downloadBlob(blob, docFileName(survey.docNo));
        toast.show(t('accounting.pdfDone', { docNo: survey.docNo }), 'success');
      })
      .catch((error: unknown) => {
        toast.show(
          error instanceof ApiClientError ? error.message : t('accounting.pdfFailed'),
          'danger',
        );
      })
      .finally(() => setPdfPendingId(null));
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('accounting.surveysTitle')}
        description={t('accounting.surveysSubtitle')}
        filtersLabel={t('accounting.filtersLabel')}
        filters={
          <Select
            variant="filter"
            label={t('accounting.filterStatus')}
            placeholder={t('accounting.filterAllStatuses')}
            value={status}
            onChange={(event) => setStatus(event.target.value as SurveyStatus | '')}
            options={SURVEY_STATUSES.map((value) => ({
              value,
              label: surveyStatusText(value),
            }))}
          />
        }
        actions={
          /* Belgi `Spinner` ga ALMASHMAYDI, o'zi aylanadi: tugmaning eni
             o'zgarmasin va odam qaysi tugmani bosganini yo'qotmasin. */
          <IconButton
            icon={<RefreshCw size={16} />}
            label={t('accounting.refresh')}
            showLabel
            spin
            busy={list.isFetching}
            onClick={() => void list.refetch()}
          />
        }
      />

      {list.isPending ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : list.isError ? (
        <Card>
          <EmptyState
            title={t('accounting.listError')}
            description={requisitionErrorText(list.error, '')}
            action={
              <Button variant="secondary" onClick={() => void list.refetch()}>
                {t('accounting.retry')}
              </Button>
            }
          />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ClipboardCheck size={20} aria-hidden />}
            title={t('accounting.surveysEmpty')}
            description={t('accounting.surveysEmptyHint')}
          />
        </Card>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
          <Card title={t('accounting.queueTitle')} padded={false}>
            <ul aria-label={t('accounting.queueCaption')} className="flex flex-col gap-1 p-2">
              {items.map((survey) => (
                <QueueRow
                  key={survey.id}
                  survey={survey}
                  selected={survey.id === selectedId}
                  onSelect={() => {
                    transition.reset();
                    setSelectedId(survey.id);
                  }}
                />
              ))}
            </ul>
          </Card>

          {selected === null ? (
            <Card>
              <EmptyState
                icon={<ClipboardCheck size={20} aria-hidden />}
                title={t('accounting.selectSurvey')}
                description={t('accounting.selectSurveyHint')}
              />
            </Card>
          ) : requisition.isPending ? (
            <Skeleton className="h-72 w-full" />
          ) : (
            <SurveyCard
              survey={selected}
              lines={lines}
              purpose={requisition.data?.purpose ?? null}
              warehouseName={
                (warehouses.data?.items ?? []).find((row) => row.id === selected.warehouseId)
                  ?.name ?? null
              }
              orgUnitName={orgUnits.nameOf(selected.orgUnitId)}
              canApprove={can('accounting.survey.approve', target)}
              canReject={can('accounting.survey.reject', target)}
              pending={transition.isPending}
              error={errorText}
              pdfPending={pdfPendingId === selected.id}
              /* Tasdiqlash QOG'OZDAN o'tadi: buxgalter imzolaydigan
                 hujjatni ko'rmasdan turib tasdiqlay olmaydi va blankdagi
                 raqamni ham o'sha yerda qo'yadi. Kartochkadagi tugma
                 endi qarorni bajarmaydi — hujjatni ochadi. */
              onApprove={() => setRequirementId(selected.id)}
              onReject={(reason) => decide('REJECTED', reason)}
              onDownloadPdf={() => downloadPdf(selected)}
              onOpenRequirement={() => setRequirementId(selected.id)}
            />
          )}
        </div>
      )}

      {/*
        KO'RIB CHIQISH oynasi sahifa DARAJASIDA.

        Ilgari bu yerda faqat blankning ko'rinishi turardi va qaror
        tugmalari kartochkada qolardi: buxgalter qog'ozni ko'rib bo'lib,
        oynani yopib, tugmani boshqa joydan izlashi kerak edi. Endi
        qog'oz, rekvizitlar, satrlar va qaror bitta joyda — blankdagi
        raqam ham shu yerda yoziladi.
      */}
      {requirementId === null || selected === null ? null : (
        <SurveyReviewDialog
          open
          survey={selected}
          onClose={() => setRequirementId(null)}
          nameOf={(id) => nomenclatureOf.get(id)}
          {...(warehouseNameOf(selected.warehouseId) === undefined
            ? {}
            : { warehouseName: warehouseNameOf(selected.warehouseId) as string })}
          canApprove={can('accounting.survey.approve', target)}
          canReject={can('accounting.survey.reject', target)}
          pending={transition.isPending}
          error={errorText}
          onApprove={() => decide('ACCOUNTING_APPROVED')}
          onReject={(reason) => decide('REJECTED', reason)}
        />
      )}
    </div>
  );
};
